<template>
  <IntercomModal
    v-model:show="showModal"
    title="New Debit Note (Purchase Return)"
    width="900px"
  >
    <div class="debit-note-form">
      <!-- Purchase Bill Selection -->
      <div class="form-row">
        <div class="form-field">
          <IntercomSelect
            v-model="formData.purchase_bill_id"
            label="Purchase Bill"
            :options="billOptions"
            placeholder="Select a finalized purchase bill"
            filterable
            :disabled="isLoadingBills"
            @update:model-value="handleBillSelect"
          />
        </div>

        <div class="form-field">
          <IntercomInput
            v-model="formData.reason"
            label="Reason"
            placeholder="Reason for return (optional)"
            type="textarea"
            :rows="2"
          />
        </div>
      </div>

      <!-- Selected Bill Info -->
      <div v-if="selectedBill" class="bill-info">
        <span class="bill-info-label">Bill:</span> {{ selectedBill.bill_number }}
        <span class="bill-info-sep">|</span>
        <span class="bill-info-label">Vendor:</span> {{ selectedBill.vendor_name }}
        <span class="bill-info-sep">|</span>
        <span class="bill-info-label">Total:</span> &#8377;{{ selectedBill.total_amount?.toFixed(2) }}
      </div>

      <!-- Return Items Section -->
      <div v-if="selectedBill && selectedBill.items && selectedBill.items.length > 0">
        <div class="section-divider">
          <span class="section-title">Return Items</span>
        </div>

        <div class="line-items-table">
          <div class="line-items-header">
            <div class="col-product">Product</div>
            <div class="col-hsn">HSN</div>
            <div class="col-orig-qty">Orig Qty</div>
            <div class="col-qty">Return Qty</div>
            <div class="col-price">Unit Price</div>
            <div class="col-gst">GST%</div>
            <div class="col-total">Total</div>
          </div>

          <div
            v-for="(item, index) in returnItems"
            :key="index"
            class="line-item-row"
            :class="{ 'row-active': item.return_qty > 0 }"
          >
            <div class="col-product">
              <span class="product-name">{{ item.product_name }}</span>
            </div>

            <div class="col-hsn">
              <span class="readonly-display">{{ item.hsn_code || '-' }}</span>
            </div>

            <div class="col-orig-qty">
              <span class="readonly-display">{{ item.original_qty }}</span>
            </div>

            <div class="col-qty">
              <input
                v-model.number="item.return_qty"
                type="number"
                min="0"
                :max="item.original_qty"
                step="0.01"
                class="item-input"
                placeholder="0"
              />
            </div>

            <div class="col-price">
              <span class="readonly-display">&#8377;{{ item.unit_price.toFixed(2) }}</span>
            </div>

            <div class="col-gst">
              <span class="readonly-display">{{ item.combined_rate > 0 ? item.combined_rate + '%' : '-' }}</span>
            </div>

            <div class="col-total">
              <span class="total-display" v-if="item.return_qty > 0">
                &#8377;{{ (item.return_qty * item.unit_price).toFixed(2) }}
              </span>
              <span v-else class="readonly-display">-</span>
            </div>
          </div>
        </div>

        <!-- Totals Section -->
        <div v-if="noteTotals.total > 0" class="totals-section">
          <div class="totals-row">
            <span class="totals-label">Subtotal (Taxable):</span>
            <span class="totals-value">&#8377;{{ noteTotals.taxable.toFixed(2) }}</span>
          </div>
          <div v-if="noteTotals.cgst > 0" class="totals-row">
            <span class="totals-label">CGST:</span>
            <span class="totals-value cgst">&#8377;{{ noteTotals.cgst.toFixed(2) }}</span>
          </div>
          <div v-if="noteTotals.sgst > 0" class="totals-row">
            <span class="totals-label">SGST:</span>
            <span class="totals-value sgst">&#8377;{{ noteTotals.sgst.toFixed(2) }}</span>
          </div>
          <div v-if="noteTotals.igst > 0" class="totals-row">
            <span class="totals-label">IGST:</span>
            <span class="totals-value igst">&#8377;{{ noteTotals.igst.toFixed(2) }}</span>
          </div>
          <div class="totals-row grand-total">
            <span class="totals-label">Return Total:</span>
            <span class="totals-value">&#8377;{{ noteTotals.total.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <div v-else-if="formData.purchase_bill_id && !isLoadingBill" class="empty-state">
        No items found on this purchase bill
      </div>
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        Cancel
      </IntercomButton>
      <IntercomButton
        variant="secondary"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="handleSubmit(false)"
      >
        Save as Draft
      </IntercomButton>
      <IntercomButton
        variant="primary"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="handleSubmit(true)"
      >
        Save & Finalize
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useToast } from '@shared/composables/useToast.js'
import { IntercomModal, IntercomButton, IntercomInput, IntercomSelect } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
})

const emit = defineEmits(['update:show', 'success'])

const toast = useToast()
const { listPurchaseBills, getPurchaseBill, createDebitNote, finalizeDebitNote } = useBilling()

// State
const showModal = ref(props.show)
const isLoadingBills = ref(false)
const isLoadingBill = ref(false)
const isSaving = ref(false)
const billOptions = ref([])
const selectedBill = ref(null)
const returnItems = ref([])

// Form data
const formData = reactive({
  purchase_bill_id: null,
  reason: '',
})

// Computed: Can submit
const canSubmit = computed(() => {
  if (!formData.purchase_bill_id) return false
  return returnItems.value.some((item) => item.return_qty > 0)
})

// Computed: Totals
const noteTotals = computed(() => {
  let taxable = 0
  let cgst = 0
  let sgst = 0
  let igst = 0

  returnItems.value.forEach((item) => {
    if (item.return_qty <= 0) return
    const lineAmount = item.return_qty * item.unit_price
    taxable += lineAmount

    const cgstRate = item.cgst_rate || 0
    const sgstRate = item.sgst_rate || 0
    const igstRate = item.igst_rate || 0
    cgst += lineAmount * cgstRate / 100
    sgst += lineAmount * sgstRate / 100
    igst += lineAmount * igstRate / 100
  })

  const totalTax = cgst + sgst + igst
  return {
    taxable,
    cgst,
    sgst,
    igst,
    total: taxable + totalTax,
  }
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val) {
      fetchBills()
      resetForm()
    }
  }
)

watch(showModal, (val) => {
  emit('update:show', val)
})

const fetchBills = async () => {
  isLoadingBills.value = true
  try {
    const response = await listPurchaseBills({ limit: 100 })
    const bills = (response.data || []).filter((b) => b.status === 'finalized')
    billOptions.value = bills.map((b) => ({
      label: `${b.bill_number} - ${b.vendor_name} - \u20B9${b.total_amount?.toFixed(2)}`,
      value: b.id,
      bill: b,
    }))
  } catch (error) {
    console.error('Failed to fetch purchase bills:', error)
    billOptions.value = []
  } finally {
    isLoadingBills.value = false
  }
}

const handleBillSelect = async (billId) => {
  if (!billId) {
    selectedBill.value = null
    returnItems.value = []
    return
  }

  isLoadingBill.value = true
  try {
    const response = await getPurchaseBill(billId)
    const bill = response.purchase_bill || response
    selectedBill.value = bill
    returnItems.value = (bill.items || []).map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      hsn_code: item.hsn_code || null,
      original_qty: parseFloat(Number(item.quantity).toFixed(3)),
      return_qty: 0,
      unit_price: item.unit_price,
      cgst_rate: item.cgst_rate || 0,
      sgst_rate: item.sgst_rate || 0,
      igst_rate: item.igst_rate || 0,
      combined_rate: (item.cgst_rate || 0) + (item.sgst_rate || 0) + (item.igst_rate || 0),
    }))
  } catch (error) {
    console.error('Failed to fetch purchase bill:', error)
    selectedBill.value = null
    returnItems.value = []
  } finally {
    isLoadingBill.value = false
  }
}

const resetForm = () => {
  formData.purchase_bill_id = null
  formData.reason = ''
  selectedBill.value = null
  returnItems.value = []
}

const handleCancel = () => {
  showModal.value = false
  resetForm()
}

const handleSubmit = async (andFinalize = false) => {
  if (!canSubmit.value) return

  isSaving.value = true
  try {
    const items = returnItems.value
      .filter((item) => item.return_qty > 0)
      .map((item) => ({
        product_id: item.product_id,
        quantity: item.return_qty,
        unit_price: item.unit_price,
        cgst_rate: item.cgst_rate || 0,
        sgst_rate: item.sgst_rate || 0,
        igst_rate: item.igst_rate || 0,
      }))

    const createResponse = await createDebitNote({
      purchase_bill_id: formData.purchase_bill_id,
      reason: formData.reason || null,
      items,
    })

    if (andFinalize) {
      const noteId = createResponse.debit_note?.id || createResponse.data?.id || createResponse.id
      if (noteId) {
        await finalizeDebitNote(noteId)
        toast.success('Debit note created and finalized')
      }
    } else {
      toast.success('Debit note created as draft')
    }

    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to create debit note:', error)
    toast.error('Failed to create debit note: ' + (error.message || 'Unknown error'))
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.debit-note-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.bill-info {
  padding: var(--space-3) var(--space-4);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.bill-info-label {
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
}

.bill-info-sep {
  margin: 0 var(--space-2);
  color: var(--color-gray-300);
}

.section-divider {
  position: relative;
  text-align: center;
  margin: var(--space-2) 0;
}

.section-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: var(--color-gray-200);
}

.section-title {
  position: relative;
  display: inline-block;
  padding: 0 var(--space-3);
  background: var(--color-white);
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-items-table {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.line-items-header {
  display: grid;
  grid-template-columns: 1fr 70px 60px 70px 80px 50px 80px;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-100);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-item-row {
  display: grid;
  grid-template-columns: 1fr 70px 60px 70px 80px 50px 80px;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
  transition: background 0.15s;
}

.col-product {
  min-width: 0;
  overflow: hidden;
}

.col-product :deep(.n-base-selection-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-hsn { text-align: center; }

.col-total { text-align: right; }

.line-item-row.row-active {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.product-name {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.readonly-display {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.item-input {
  width: 100%;
  height: 36px;
  padding: 0 var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  outline: none;
  text-align: right;
  transition: all var(--transition-fast);
}

.item-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(0, 87, 255, 0.1);
}

.total-display {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.totals-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  align-self: flex-end;
  min-width: 300px;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) 0;
}

.totals-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  font-weight: var(--font-weight-medium);
}

.totals-value {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-semibold);
}

.totals-value.cgst { color: #2563eb; }
.totals-value.sgst { color: #7c3aed; }
.totals-value.igst { color: #0891b2; }

.totals-row.grand-total {
  border-top: 2px solid var(--color-gray-300);
  padding-top: var(--space-3);
  margin-top: var(--space-2);
}

.totals-row.grand-total .totals-label {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-bold);
}

.totals-row.grand-total .totals-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

.empty-state {
  text-align: center;
  padding: var(--space-8);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
